import React, { useEffect, useMemo, useRef, useState } from "react";
import {
    Box,
    CardHeader,
    Center,
    Checkbox,
    Flex,
    HStack,
    Icon,
    Progress,
    SimpleGrid,
    Text,
    useColorMode,
    VStack,
    Badge,
    Button,
    Tooltip,
    Spinner,
} from "@chakra-ui/react";
import { FiAlertTriangle, FiCheckCircle, FiClock, FiRefreshCw, FiX } from "react-icons/fi";
import { TbTimelineEventPlus } from "react-icons/tb";
import Primary from "../../../../Components/Buttons/Primary";
import dayjs from "../../../../Components/Functions/dayjs";
import secFormatter from "../../../../Components/Functions/formatters/secFormatter";
import MyCard from "../../../../Components/micro/Card";
import Table from "../../../../Components/micro/Table";
import Categorize from "../../../Targets/Target/Downtime/Categorize";
import { downtimeCategoriesType } from "../../../Targets/Target/Downtime/categories";
import store from "../../../../Components/Store";
import { dateAtom, shiftsAtom, uidAtom } from "../../../../Components/Store/atoms";
import Listen from "../../../../Components/Store/hooks/Listen";
import Get from "../../../../Components/Store/hooks/Get";
import { db } from "../../../../Components/firebase";
import { ref, onValue, query, orderByChild, startAt } from "firebase/database";

export interface MachineDowntimeEvent {
    id: string;
    start: number;
    end: number;
    duration: number;
    status: "IDLE" | "OFF";
    category: string;
    note: string;
    scheduled: boolean;
    isActive?: boolean;
}

interface Props {
    machineId: string;
    machineName?: string;
    date?: string;
    machineStatus?: "ON" | "IDLE" | "OFF" | string;
    onUpdateLog?: () => void;
}

const headings = [
    "Time",
    "Status",
    "Duration",
    "Category",
    "Type",
    "Notes",
    "Action"
];

const SelectCell: React.FC<{ checked: boolean; onToggle: (checked: boolean, shiftKey: boolean) => void }> = ({ checked, onToggle }) => {
    return (
        <Checkbox
            colorScheme="orange"
            isChecked={checked}
            aria-label="Select downtime"
            onChange={e => onToggle(e.target.checked, !!(e.nativeEvent as MouseEvent).shiftKey)}
        />
    );
};

const TimeCell: React.FC<{ start: number; end: number }> = ({ start, end }) => {
    const timeLabel = `${dayjs.unix(start).format("hh:mm A")} - ${dayjs.unix(end).format("hh:mm A")}`;
    const dateLabel = dayjs.unix(start).format("DD MMM, YYYY");
    return (
        <VStack align="start" spacing={0} py={1}>
            <Text fontSize="xs" fontWeight={600}>{timeLabel}</Text>
            <Text fontSize="2xs" color="gray.500">{dateLabel}</Text>
        </VStack>
    );
};

const DurationCell: React.FC<{ duration: number; isActive?: boolean }> = ({ duration, isActive }) => {
    return (
        <HStack spacing={1.5} py={1}>
            <Text fontSize="xs" fontWeight={600}>{secFormatter(duration)}</Text>
            {isActive && (
                <Badge colorScheme="red" variant="solid" fontSize="3xs" px={1} borderRadius="sm">
                    ACTIVE
                </Badge>
            )}
        </HStack>
    );
};

const DowntimeCard: React.FC<Props> = ({
    machineId,
    machineName,
    date: propDate,
    machineStatus,
    onUpdateLog
}) => {
    const { colorMode } = useColorMode();
    const isDark = colorMode === "dark";
    const selectedDate = Listen(dateAtom);
    const shiftsInfo = Get(shiftsAtom);
    const date = propDate || selectedDate || dayjs().format("YYYY-MM-DD");

    const [events, setEvents] = useState<MachineDowntimeEvent[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [refreshTrigger, setRefreshTrigger] = useState<number>(0);
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const lastTicked = useRef<number | null>(null);

    const { border, subText } = useMemo(() => ({
        border: `${colorMode}.border`,
        subText: `${colorMode}.subText`
    }), [colorMode]);

    const selectedEvents = useMemo(
        () => events.filter(ev => selectedIds.includes(ev.id)),
        [events, selectedIds]
    );
    const selectedTotal = useMemo(
        () => selectedEvents.reduce((acc, ev) => acc + ev.duration, 0),
        [selectedEvents]
    );
    const uncategorizedEvents = useMemo(
        () => events.filter(ev => !ev.category || ev.category.toLowerCase() === "uncategorized"),
        [events]
    );
    const allUncategorizedSelected = uncategorizedEvents.length > 0 && uncategorizedEvents.every(ev => selectedIds.includes(ev.id));

    const toggleRow = (index: number, checked: boolean, shiftKey: boolean) => {
        const from = shiftKey && lastTicked.current !== null ? Math.min(lastTicked.current, index) : index;
        const to = shiftKey && lastTicked.current !== null ? Math.max(lastTicked.current, index) : index;
        const ids = events.slice(from, to + 1).map(ev => ev.id);
        setSelectedIds(prev => checked
            ? Array.from(new Set([...prev, ...ids]))
            : prev.filter(id => !ids.includes(id)));
        lastTicked.current = index;
    };

    const toggleAllUncategorized = () => {
        const ids = uncategorizedEvents.map(ev => ev.id);
        setSelectedIds(prev => allUncategorizedSelected
            ? prev.filter(id => !ids.includes(id))
            : Array.from(new Set([...prev, ...ids])));
        lastTicked.current = null;
    };

    const handleBulkSuccess = (updatedList: any[]) => {
        setEvents(prev => prev.map(ev => {
            const match = updatedList.find(u => u.id === ev.id);
            if (match) {
                return {
                    ...ev,
                    category: match.reason?.category || ev.category,
                    note: match.reason?.note || "",
                    scheduled: !!match.reason?.scheduled
                };
            }
            return ev;
        }));
        setSelectedIds([]);
        onUpdateLog?.();
    };

    const tableHeadings = useMemo(() => [
        <Checkbox
            key="select-all"
            colorScheme="orange"
            aria-label="Select all uncategorized downtime"
            isDisabled={uncategorizedEvents.length === 0}
            isChecked={allUncategorizedSelected}
            isIndeterminate={selectedEvents.length > 0 && !allUncategorizedSelected}
            onChange={toggleAllUncategorized}
        />,
        ...headings
        // eslint-disable-next-line react-hooks/exhaustive-deps
    ], [uncategorizedEvents.length, allUncategorizedSelected, selectedEvents.length]);

    useEffect(() => {
        if (!machineId) {
            setEvents([]);
            setLoading(false);
            return;
        }

        const uid = store.get(uidAtom);
        if (!uid) {
            setLoading(false);
            return;
        }

        setLoading(true);

        // Production day window: starts at shift A start (if configured) or midnight
        const dayStartUnix = dayjs(`${date} 00:00:00`).unix();
        const shiftAStartSec = shiftsInfo?.A ? +shiftsInfo.A : 0;
        const windowStart = dayStartUnix + shiftAStartSec;
        const windowEnd = windowStart + 86400;

        // Query downtime records starting from 1 day before to catch cross-boundary stoppages
        const fetchStart = windowStart - 86400;
        const downtimeRef = query(
            ref(db, `users/${uid}/downtime/${machineId}`),
            orderByChild("start"),
            startAt(fetchStart)
        );

        const unsubscribe = onValue(downtimeRef, (snapshot) => {
            if (!snapshot.exists()) {
                setEvents([]);
                setLoading(false);
                return;
            }

            const rawData = snapshot.val() as Record<string, {
                start: number;
                end?: number;
                status?: "IDLE" | "OFF";
                reason?: {
                    category: downtimeCategoriesType;
                    note: string;
                    scheduled: boolean;
                } | string;
            }>;

            const nowUnix = dayjs().unix();
            const list: MachineDowntimeEvent[] = [];

            for (const [id, item] of Object.entries(rawData)) {
                if (!item || typeof item.start !== "number" || isNaN(item.start)) continue;

                const isActive = !item.end || item.end === 0;
                // If machine is currently ON, an unclosed stoppage was clamped
                const rawEnd = isActive
                    ? (machineStatus === "ON" ? Math.min(nowUnix, windowEnd) : nowUnix)
                    : (item.end || nowUnix);

                // Check overlap with the production day window
                if (Math.max(item.start, windowStart) < Math.min(rawEnd, windowEnd)) {
                    const clampedStart = Math.max(item.start, windowStart);
                    const clampedEnd = Math.min(rawEnd, windowEnd);
                    const duration = Math.max(clampedEnd - clampedStart, 0);

                    // Extract categorization reason
                    let category = "uncategorized";
                    let note = "";
                    let scheduled = false;

                    if (item.reason) {
                        if (typeof item.reason === "string") {
                            note = item.reason;
                        } else if (typeof item.reason === "object") {
                            category = item.reason.category || "uncategorized";
                            note = item.reason.note || "";
                            scheduled = !!item.reason.scheduled;
                        }
                    }

                    list.push({
                        id,
                        start: clampedStart,
                        end: clampedEnd,
                        duration,
                        status: item.status === "OFF" ? "OFF" : "IDLE",
                        category,
                        note,
                        scheduled,
                        isActive: isActive && (machineStatus !== "ON")
                    });
                }
            }

            // Sort chronologically
            list.sort((a, b) => a.start - b.start);
            setEvents(list);
            setLoading(false);
        }, (err) => {
            console.error("Failed to load machine downtimes:", err);
            setLoading(false);
        });

        return () => {
            unsubscribe();
        };
    }, [machineId, date, shiftsInfo, machineStatus, refreshTrigger]);

    // KPI Metrics
    const metrics = useMemo(() => {
        let total = 0;
        let planned = 0;
        let unplanned = 0;
        let uncategorized = 0;
        const categoryMap: Record<string, number> = {};

        for (const ev of events) {
            total += ev.duration;
            const cat = ev.category.trim().toLowerCase();
            const isUncat = !cat || cat === "uncategorized" || cat === "none";

            if (isUncat) {
                uncategorized += ev.duration;
                categoryMap["uncategorized"] = (categoryMap["uncategorized"] || 0) + ev.duration;
            } else {
                if (ev.scheduled) {
                    planned += ev.duration;
                } else {
                    unplanned += ev.duration;
                }
                categoryMap[cat] = (categoryMap[cat] || 0) + ev.duration;
            }
        }

        const breakdown = Object.entries(categoryMap).map(([name, value]) => ({
            name,
            value
        })).sort((a, b) => b.value - a.value);

        const categorizedDuration = total - uncategorized;
        const categorizedPercent = total > 0 ? Math.min(100, Math.round((categorizedDuration / total) * 100)) : 100;

        return {
            total,
            planned,
            unplanned,
            uncategorized,
            breakdown,
            categorizedDuration,
            categorizedPercent
        };
    }, [events]);

    const handleUpdateLog = (updated: any) => {
        setEvents(prev => prev.map(ev => {
            if (ev.id === updated.id) {
                return {
                    ...ev,
                    category: updated.reason?.category || ev.category,
                    note: updated.reason?.note || "",
                    scheduled: !!updated.reason?.scheduled
                };
            }
            return ev;
        }));
        onUpdateLog?.();
    };

    const tableRows = useMemo(() => {
        return events.map((ev, index) => {
            const isUncat = !ev.category || ev.category.toLowerCase() === "uncategorized";

            return [
                {
                    element: SelectCell,
                    props: {
                        checked: selectedIds.includes(ev.id),
                        onToggle: (checked: boolean, shiftKey: boolean) => toggleRow(index, checked, shiftKey)
                    }
                },
                {
                    element: TimeCell,
                    props: {
                        start: ev.start,
                        end: ev.end
                    }
                },
                {
                    element: Badge,
                    props: {
                        colorScheme: ev.status === "OFF" ? "red" : "orange",
                        variant: "subtle",
                        fontSize: "2xs",
                        borderRadius: "sm"
                    },
                    children: ev.status
                },
                {
                    element: DurationCell,
                    props: {
                        duration: ev.duration,
                        isActive: ev.isActive
                    }
                },
                {
                    element: Text,
                    props: {
                        fontSize: "xs",
                        fontWeight: 600,
                        color: isUncat ? "red.400" : (ev.scheduled ? "primary" : undefined),
                        textTransform: "uppercase"
                    },
                    children: ev.category
                },
                {
                    element: Badge,
                    props: {
                        colorScheme: ev.scheduled ? "blue" : "gray",
                        variant: "subtle",
                        fontSize: "2xs",
                        borderRadius: "sm"
                    },
                    children: ev.scheduled ? "Planned (No Loss)" : "Unplanned"
                },
                {
                    element: Text,
                    props: { fontSize: "2xs", color: subText },
                    children: ev.note && ev.note !== "None" ? ev.note : "-"
                },
                {
                    element: Categorize,
                    props: {
                        machineId,
                        downtime: {
                            id: ev.id,
                            start: ev.start,
                            end: ev.end,
                            status: ev.status,
                            reason: {
                                category: ev.category,
                                note: ev.note,
                                scheduled: ev.scheduled
                            }
                        },
                        updateLog: handleUpdateLog
                    }
                }
            ];
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [events, selectedIds, machineId, subText]);

    return (
        <MyCard w="100%" noPadding>
            <CardHeader
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                py={3}
                px={4}
                borderBottom="1px solid"
                borderColor={border}
            >
                <HStack spacing={2}>
                    <Icon as={FiClock} color="primary" fontSize="md" />
                    <Text fontWeight={700} fontSize="md">Downtime Monitoring</Text>
                    <Badge colorScheme={events.length > 0 ? "orange" : "green"} variant="subtle" fontSize="xs" borderRadius="md" px={2}>
                        {events.length} Stoppage{events.length === 1 ? "" : "s"}
                    </Badge>
                    {metrics.uncategorized > 0 && (
                        <Badge colorScheme="red" variant="solid" fontSize="2xs" borderRadius="md" px={1.5}>
                            {events.filter(e => !e.category || e.category.toLowerCase() === "uncategorized").length} Uncategorized
                        </Badge>
                    )}
                </HStack>
                <HStack spacing={2}>
                    <Tooltip label="Refresh downtime logs">
                        <Button
                            size="xs"
                            variant="ghost"
                            onClick={() => setRefreshTrigger(prev => prev + 1)}
                            isLoading={loading}
                            leftIcon={<FiRefreshCw />}
                        >
                            Refresh
                        </Button>
                    </Tooltip>
                </HStack>
            </CardHeader>

            <Box p={4}>
                {loading && events.length === 0 ? (
                    <Center py={10}>
                        <Spinner color="primary" size="md" mr={3} />
                        <Text fontSize="sm" color={subText}>Loading downtime records...</Text>
                    </Center>
                ) : events.length === 0 ? (
                    <Center p={8} flexDir="column">
                        <Box
                            p={3}
                            borderRadius="full"
                            bg={isDark ? "whiteAlpha.100" : "green.50"}
                            color="green.400"
                            mb={2}
                        >
                            <Icon as={FiCheckCircle} fontSize="2xl" />
                        </Box>
                        <Text fontWeight={700} fontSize="sm">
                            Zero Stoppages Recorded Today
                        </Text>
                        <Text fontSize="xs" color={subText}>
                            This machine has operated with zero recorded stoppage interruptions for this production day.
                        </Text>
                    </Center>
                ) : (
                    <VStack spacing={4} align="stretch">
                        {/* KPI Summary Cards */}
                        <SimpleGrid columns={{ base: 2, md: 4 }} gap={3}>
                            <MyCard p={3}>
                                <HStack justify="space-between" align="center">
                                    <Text fontSize="3xs" textTransform="uppercase" fontWeight={700} color={subText}>Total Downtime</Text>
                                    <Icon as={FiAlertTriangle} color="red.400" />
                                </HStack>
                                <Text fontSize="lg" fontWeight={700} mt={1}>{secFormatter(metrics.total)}</Text>
                                <Text fontSize="3xs" color={subText}>{events.length} Stoppage Chunk(s)</Text>
                            </MyCard>

                            <MyCard p={3}>
                                <HStack justify="space-between" align="center">
                                    <Text fontSize="3xs" textTransform="uppercase" fontWeight={700} color={subText}>Planned Downtime</Text>
                                    <Badge colorScheme="blue" variant="subtle" fontSize="3xs">No Loss</Badge>
                                </HStack>
                                <Text fontSize="lg" fontWeight={700} mt={1} color="blue.400">{secFormatter(metrics.planned)}</Text>
                                <Text fontSize="3xs" color={subText}>Off-shift & scheduled maintenance</Text>
                            </MyCard>

                            <MyCard p={3}>
                                <HStack justify="space-between" align="center">
                                    <Text fontSize="3xs" textTransform="uppercase" fontWeight={700} color={subText}>Unplanned Downtime</Text>
                                    <Badge colorScheme="red" variant="subtle" fontSize="3xs">Loss</Badge>
                                </HStack>
                                <Text fontSize="lg" fontWeight={700} mt={1} color="red.400">{secFormatter(metrics.unplanned)}</Text>
                                <Text fontSize="3xs" color={subText}>Penalizes operational efficiency</Text>
                            </MyCard>

                            <MyCard p={3}>
                                <HStack justify="space-between" align="center">
                                    <Text fontSize="3xs" textTransform="uppercase" fontWeight={700} color={subText}>Categorized</Text>
                                    <Text fontSize="3xs" fontWeight={700} color={metrics.categorizedPercent >= 80 ? "green.400" : "orange.400"}>
                                        {metrics.categorizedPercent}%
                                    </Text>
                                </HStack>
                                <Progress
                                    mt={2}
                                    size="xs"
                                    borderRadius="full"
                                    value={metrics.categorizedPercent}
                                    colorScheme={metrics.categorizedPercent >= 80 ? "green" : "orange"}
                                />
                                <Text fontSize="3xs" color={subText} mt={1.5}>
                                    {secFormatter(metrics.categorizedDuration)} of {secFormatter(metrics.total)}
                                </Text>
                            </MyCard>
                        </SimpleGrid>

                        {/* Breakdown & Table */}
                        <SimpleGrid columns={{ base: 1, lg: 3 }} gap={3}>
                            {/* Category Breakdown (1 col) */}
                            <MyCard p={4}>
                                <Text fontWeight={700} fontSize="sm" mb={3}>Category Distribution</Text>
                                <VStack spacing={2.5} align="stretch" maxH="320px" overflowY="auto">
                                    {metrics.breakdown.map(cat => {
                                        const pct = metrics.total > 0 ? Math.round((cat.value / metrics.total) * 100) : 0;
                                        const isUncat = cat.name.toLowerCase() === "uncategorized";
                                        return (
                                            <Box key={cat.name} p={2} borderRadius="md" bg={isDark ? "whiteAlpha.50" : "gray.50"}>
                                                <HStack justify="space-between" align="baseline">
                                                    <Text fontSize="xs" fontWeight={600} color={isUncat ? "red.400" : undefined}>
                                                        {cat.name.toUpperCase()}
                                                    </Text>
                                                    <HStack spacing={1.5}>
                                                        <Text fontSize="xs" fontWeight={700}>{secFormatter(cat.value)}</Text>
                                                        <Text fontSize="3xs" color={subText}>({pct}%)</Text>
                                                    </HStack>
                                                </HStack>
                                                <Progress
                                                    mt={1.5}
                                                    size="xs"
                                                    borderRadius="full"
                                                    value={pct}
                                                    colorScheme={isUncat ? "red" : (cat.name === "off-shift" ? "blue" : "orange")}
                                                />
                                            </Box>
                                        );
                                    })}
                                </VStack>
                            </MyCard>

                            {/* Stoppages Log Table (2 cols) */}
                            <Box gridColumn={{ base: "span 1", lg: "span 2" }}>
                                <MyCard p={0} overflow="hidden">
                                    <CardHeader py={3} px={4} borderBottom="1px solid" borderColor={border}>
                                        <HStack justify="space-between">
                                            <Text fontWeight={700} fontSize="sm">Hourly Stoppage Chunks & Reasons</Text>
                                            <Badge colorScheme="orange" variant="outline" fontSize="2xs">
                                                {events.length} Records
                                            </Badge>
                                        </HStack>
                                    </CardHeader>
                                    {/* Multi-Select Action Banner */}
                                    <Flex
                                        justify="space-between"
                                        align="center"
                                        px={4}
                                        py={2}
                                        borderBottom="1px solid"
                                        borderColor={border}
                                        bg={selectedEvents.length > 0 ? (isDark ? "whiteAlpha.100" : "orange.50") : undefined}
                                    >
                                        {selectedEvents.length > 0 ? (
                                            <>
                                                <HStack spacing={2}>
                                                    <Text fontWeight={600} fontSize="xs" color="orange.400">
                                                        {selectedEvents.length} selected
                                                        <Text as="span" fontWeight={400} opacity={0.7}> | {secFormatter(selectedTotal)}</Text>
                                                    </Text>
                                                    {uncategorizedEvents.length > 0 && !allUncategorizedSelected && (
                                                        <Button size="xs" variant="link" colorScheme="orange" onClick={toggleAllUncategorized}>
                                                            Select all uncategorized ({uncategorizedEvents.length})
                                                        </Button>
                                                    )}
                                                </HStack>
                                                <HStack spacing={2}>
                                                    <Button size="xs" variant="ghost" leftIcon={<FiX />} onClick={() => setSelectedIds([])}>
                                                        Clear
                                                    </Button>
                                                    <Categorize
                                                        machineId={machineId}
                                                        bulkDowntimes={selectedEvents.map(ev => ({
                                                            id: ev.id,
                                                            start: ev.start,
                                                            end: ev.end,
                                                            status: ev.status,
                                                            reason: {
                                                                category: ev.category,
                                                                note: ev.note,
                                                                scheduled: ev.scheduled
                                                            }
                                                        }))}
                                                        onBulkSuccess={handleBulkSuccess}
                                                    >
                                                        <Primary size="xs" leftIcon={<TbTimelineEventPlus />}>
                                                            Categorize selected
                                                        </Primary>
                                                    </Categorize>
                                                </HStack>
                                            </>
                                        ) : (
                                            <>
                                                <Text fontSize="xs" opacity={0.7}>
                                                    Tick rows to categorize several at once (shift + click selects a range).
                                                </Text>
                                                {uncategorizedEvents.length > 0 && (
                                                    <Button size="xs" variant="outline" colorScheme="orange" onClick={toggleAllUncategorized}>
                                                        Select all uncategorized ({uncategorizedEvents.length})
                                                    </Button>
                                                )}
                                            </>
                                        )}
                                    </Flex>
                                    <Box maxH="320px" overflow="auto">
                                        <Table
                                            headings={tableHeadings}
                                            notFound={events.length === 0}
                                            rows={tableRows}
                                        />
                                    </Box>
                                </MyCard>
                            </Box>
                        </SimpleGrid>
                    </VStack>
                )}
            </Box>
        </MyCard>
    );
};

export default DowntimeCard;
